// frontend/src/pages/CircleAdminPage.js

import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import { api } from "api";
import { X } from "phosphor-react";
import BackButton from "../components/BackButton";
import { DEFAULT_PROFILE_IMAGE } from "../utils/blank-image";

const SmallTag = ({ children, tone = "gray" }) => {
  const toneClass =
    tone === "blue"
      ? "bg-blue-200 text-blue-800"
      : tone === "red"
      ? "bg-red-100 text-red-800"
      : "bg-blue-200 text-blue-800";

  return (
    <span className={`inline-block text-xs !min-h-0 !min-w-0 px-2.5 py-1 rounded-full ${toneClass}`}>
      {children}
    </span>
  );
};

const SmallLabelTag = ({ children, tone = "gray" }) => {
  const toneClass =
    tone === "blue"
      ? "bg-blue-200 text-blue-800"
      : tone === "red"
      ? "bg-red-100 text-red-800"
      : "bg-gray-200 text-gray-900";

  return (
    <span className={`inline-block text-xs !min-h-0 !min-w-0 px-2.5 py-0.5 rounded-full ${toneClass}`}>
      {children}
    </span>
  );
};

const SmallNumberTag = ({ children, tone = "gray" }) => {
  const toneClass =
    tone === "blue"
      ? "bg-blue-600 text-white"
      : tone === "red"
      ? "bg-red-100 text-red-800"
      : "bg-gray-200 text-gray-900";

  return (
    <span
      className={`inline-block font-bold text-xs !min-h-0 !min-w-0 px-2 py-0.5 rounded-full ${toneClass}`}
    >
      {children}
    </span>
  );
};

const Row = ({ children }) => (
  <div className="flex items-center justify-between gap-3 px-3 py-2 last:border-b-0">
    {children}
  </div>
);

const displayName = (u) =>
  u?.display_name || u?.name || u?.username || `User #${u?.id || "?"}`;

function ConfirmDialog({
  open,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  tone = "blue",
  onConfirm,
  onCancel,
}) {
  if (!open) return null;

  const btnTone =
    tone === "red"
      ? "bg-red-600 hover:bg-red-700"
      : "bg-blue-600 hover:bg-blue-700";

  return (
    <div className="fixed inset-0 z-[100] flex items-end md:items-center justify-center px-8 py-5">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onCancel}
        aria-hidden="true"
      />

      <div className="relative w-full max-w-sm md:max-w-md bg-white rounded-full shadow-[0_8px_0px_rgba(37,99,235,0.96)] p-5 md:p-6">
        <div className="flex items-start justify-center gap-4">
          <div>
            {/*{title && (
              <h4 className="text-lg font-semibold text-blue-700">{title}</h4>
            )}*/}
            <p className="mt-1 text-sm font-medium text-blue-800">{message}</p>
          </div>
        </div>

        <div className="mt-4 flex items-center justify-center gap-2">
          <button
            onClick={onCancel}
            className="!border !border-blue-800 !text-sm !rounded-full !px-3 !min-h-0 !h-9"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`!border-none !text-sm !rounded-full !text-white !px-3 !min-h-0 !h-9 ${btnTone}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

function useToasts() {
  const [toasts, setToasts] = useState([]);

  const push = (t) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev, { id, ...t }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((x) => x.id !== id));
    }, t?.duration ?? 2800);
  };

  const remove = (id) => setToasts((prev) => prev.filter((x) => x.id !== id));

  return { toasts, push, remove };
}

function ToastViewport({ toasts, remove }) {
  if (!toasts.length) return null;

  return (
    <div className="fixed inset-x-0 top-3 z-[110] flex flex-col items-center gap-2 px-3">
      {toasts.map((t) => {
        const tone =
          t.type === "error"
            ? "bg-red-600 text-white"
            : "bg-gray-900 text-white";

        return (
          <div
            key={t.id}
            className={`max-w-md w-full rounded-2xl shadow-lg ${tone} px-4 py-2.5 text-sm`}
            role="status"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="whitespace-pre-wrap">{t.message}</div>
              <button
                onClick={() => remove(t.id)}
                className="opacity-80 hover:opacity-100"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <X size={16} weight="bold" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function CircleAdminPage({
  groupId: groupIdProp,
  meId,
  onChanged,
}) {
  const params = useParams();
  const [currentUserId, setCurrentUserId] = useState(meId || null);
  const groupId = groupIdProp || params.groupId || params.id;

  const [loading, setLoading] = useState(false);
  const [admins, setAdmins] = useState([]);
  const [members, setMembers] = useState([]);
  const [pending, setPending] = useState(null);
  const [error, setError] = useState("");

  const [adminSearch, setAdminSearch] = useState("");
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchBoxRef = useRef(null);
  const resultsRef = useRef(null);

  const [confirm, setConfirm] = useState({
    open: false,
    title: "",
    message: "",
    tone: "blue",
    confirmText: "Confirm",
    onConfirm: null,
  });

  const { toasts, push, remove } = useToasts();

  useEffect(() => {
    if (meId) {
      setCurrentUserId(meId);
      return;
    }

    const loadCurrentUser = async () => {
      try {
        const response = await api.get("/users/me");
        setCurrentUserId(response.data?.id);
      } catch (error) {
        console.error("Could not load current user:", error);
      }
    };

    loadCurrentUser();
  }, [meId]);

  useEffect(() => {
    const onDocClick = (e) => {
      const box = searchBoxRef.current;
      const list = resultsRef.current;
      if (box && box.contains(e.target)) return;
      if (list && list.contains(e.target)) return;
      setShowSearchResults(false);
    };

    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const loadAll = async () => {
    if (!groupId) return;
    setLoading(true);
    setError("");

    try {
      const [adminsRes, membersRes] = await Promise.all([
        api.get(`/groups/${groupId}/admins`),
        api.get(`/groups/${groupId}/members`),
      ]);

      setAdmins(Array.isArray(adminsRes.data) ? adminsRes.data : []);
      setMembers(Array.isArray(membersRes.data) ? membersRes.data : []);

      try {
        const r = await api.get(`/groups/${groupId}/join-requests`);
        setPending(Array.isArray(r.data) ? r.data : []);
      } catch {
        setPending(null);
      }
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Failed to load admins/members. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId]);

  const refresh = async () => {
    await loadAll();
    if (typeof onChanged === "function") onChanged();
  };

  const promoteToAdmin = async (userId) => {
    if (!userId) return;

    try {
      await api.post(`/groups/${groupId}/admins/${userId}`, {});
      await refresh();
      setAdminSearch("");
      setShowSearchResults(false);
    } catch (e) {
      push({
        type: "error",
        message:
          e?.response?.data?.detail || "Could not add admin. Are they a member?",
      });
    }
  };

  const demoteAdmin = async (userId) => {
    try {
      await api.delete(`/groups/${groupId}/admins/${userId}`);
      await refresh();
    } catch (e) {
      push({
        type: "error",
        message: e?.response?.data?.detail || "Could not remove admin",
      });
    }
  };

  const removeMember = async (userId) => {
    try {
      await api
        .delete(`/groups/${groupId}/members/${userId}`)
        .catch(() => api.post(`/groups/${groupId}/leave/${userId}`, {}));
      await refresh();
      push({ type: "success", message: "Member removed." });
    } catch (e) {
      push({
        type: "error",
        message: e?.response?.data?.detail || "Could not remove member.",
      });
    }
  };

  const approveRequest = async (userId) => {
    try {
      await api
        .post(`/groups/${groupId}/join-requests/${userId}/approve`, {})
        .catch(() =>
          api.post(`/groups/${groupId}/join-requests/${userId}`, {
            action: "approve",
          })
        );
      await refresh();
      push({ type: "success", message: "Request approved." });
    } catch (e) {
      push({
        type: "error",
        message: e?.response?.data?.detail || "Could not approve request.",
      });
    }
  };

  const denyRequest = async (userId) => {
    try {
      await api
        .post(`/groups/${groupId}/join-requests/${userId}/deny`, {})
        .catch(() => api.delete(`/groups/${groupId}/join-requests/${userId}`))
        .catch(() =>
          api.post(`/groups/${groupId}/join-requests/${userId}`, {
            action: "deny",
          })
        );
    } catch (e) {
      push({
        type: "error",
        message: e?.response?.data?.detail || "Could not deny request.",
      });
    } finally {
      await refresh();
      push({ type: "success", message: "Request denied." });
    }
  };

  const adminIds = useMemo(
    () => new Set(admins.map((a) => String(a.id))),
    [admins]
  );

  const normalized = (s) => (s || "").toString().trim().toLowerCase();

  const filteredMemberOptions = useMemo(() => {
    const q = normalized(adminSearch);
    const pool = members.filter((m) => !adminIds.has(String(m.id)));

    if (!q) return pool.slice(0, 10);

    return pool
      .filter((m) => {
        const fields = [m?.display_name, m?.name, m?.username, m?.email];
        return fields.some((f) => normalized(f).includes(q));
      })
      .slice(0, 10);
  }, [adminSearch, members, adminIds]);

  const askConfirm = ({
    title,
    message,
    tone = "blue",
    confirmText = "Confirm",
    onConfirm,
  }) => {
    setConfirm({
      open: true,
      title,
      message,
      tone,
      confirmText,
      onConfirm,
    });
  };

  const closeConfirm = () => setConfirm((c) => ({ ...c, open: false }));

  const onPickCandidate = (u) => {
    const name = displayName(u);

    askConfirm({
      title: "Promote to admin?",
      message: `Make ${name} an admin?`,
      tone: "blue",
      confirmText: "Make admin",
      onConfirm: () => {
        closeConfirm();
        promoteToAdmin(u.id);
      },
    });
  };

  return (
    <div className="app-shell bg-blue-50">
      <header
        className="sticky top-0 left-0 right-0 z-30 bg-blue-50"
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="mt-2 mx-auto px-4 flex items-center">
          <BackButton className="text-blue-600" ariaLabel="Back" />
            <h1 className="text-[20px] text-blue-600 dm-font font-black tracking-[-0.06em] leading-[0.9] select-none">
              Manage Circle
            </h1>
        </div>
      </header>

      <main className="mx-auto w-[96%] bg-blue-50 pt-2 px-4 pb-8">
        <div className="space-y-5">
          {loading && <p className="text-sm text-gray-500">Loading…</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}

          <section className="space-y-2">
            <div className="flex items-center justify-between mr-3">
              <h3 className="text-sm font-medium ml-3">Admins</h3>
              <SmallNumberTag tone="blue">{admins.length}</SmallNumberTag>
            </div>

            <div className="rounded-2xl py-1 overflow-hidden bg-white">
              {admins.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">No admins yet</div>
              ) : (
                admins.map((u) => (
                  <Row key={`admin-${u.id}`}>
                    <div className="flex items-center gap-3 min-w-0">
                      <img
                        src={u.profile_pic_url || DEFAULT_PROFILE_IMAGE}
                        alt={displayName(u)}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                      <div className="min-w-0">
                        <div className="text-sm font-medium truncate">
                          {displayName(u)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {String(u.id) === String(currentUserId) ? (
                        <SmallLabelTag>you</SmallLabelTag>
                      ) : (
                        <button
                          className="text-xs !min-h-0 !min-w-0 px-2.5 py-1 rounded-full bg-red-100 text-red-800 hover:bg-red-200"
                          onClick={() =>
                            askConfirm({
                              title: "Remove admin?",
                              message: `Remove ${displayName(u)} as admin?`,
                              tone: "red",
                              confirmText: "Remove",
                              onConfirm: () => {
                                closeConfirm();
                                demoteAdmin(u.id);
                              },
                            })
                          }
                        >
                          Remove admin
                        </button>
                      )}
                    </div>
                  </Row>
                ))
              )}
            </div>

            <div className="pt-1" ref={searchBoxRef}>
              <label className="block text-sm font-medium mb-1 ml-3">
                Add admin by typing a member's name
              </label>

              <div className="relative">
                <input
                  className="w-full border rounded-2xl px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600 bg-white"
                  placeholder="Start typing member's name"
                  value={adminSearch}
                  onFocus={() => setShowSearchResults(true)}
                  onChange={(e) => {
                    setAdminSearch(e.target.value);
                    setShowSearchResults(true);
                  }}
                />

                {showSearchResults && (
                  <div
                    ref={resultsRef}
                    className="absolute z-10 mt-1 w-full bg-white border rounded-2xl shadow-lg max-h-64 overflow-auto"
                  >
                    {filteredMemberOptions.length === 0 ? (
                      <div className="px-3 py-2 text-sm text-gray-500">
                        No matching members
                      </div>
                    ) : (
                      filteredMemberOptions.map((u) => (
                        <button
                          key={`cand-${u.id}`}
                          type="button"
                          onClick={() => onPickCandidate(u)}
                          className="w-full flex items-center gap-3 px-3 py-2 hover:bg-blue-50 text-left"
                        >
                          <img
                            src={u.profile_pic_url || DEFAULT_PROFILE_IMAGE}
                            alt={displayName(u)}
                            className="w-8 h-8 rounded-full object-cover ring-1 ring-blue-100"
                          />
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">
                              {displayName(u)}
                            </div>
                            {u?.username && (
                              <div className="text-xs text-gray-500 truncate">
                                @{u.username}
                              </div>
                            )}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>

              <p className="mt-1 text-xs text-gray-500 ml-3">
                Only existing members appear here
              </p>
            </div>
          </section>

          <section className="space-y-2">
            <div className="flex items-center mr-3 justify-between">
              <h3 className="text-sm font-medium ml-3">Members</h3>
              <SmallNumberTag tone="blue">{members.length}</SmallNumberTag>
            </div>

            <div className="rounded-2xl py-1 max-h-64 overflow-auto bg-white">
              {members.length === 0 ? (
                <div className="p-3 text-sm text-gray-500">No members yet</div>
              ) : (
                members.map((u) => {
                  const isAdmin = admins.some(
                    (a) => String(a.id) === String(u.id)
                  );

                  const isCurrentUser =
                    currentUserId != null &&
                    String(u.id) === String(currentUserId);

                  return (
                    <Row key={`member-${u.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={u.profile_pic_url || DEFAULT_PROFILE_IMAGE}
                          alt={displayName(u)}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {displayName(u)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {isCurrentUser ? (
                          <>
                            <SmallLabelTag>you</SmallLabelTag>
                            <SmallLabelTag tone="blue">admin</SmallLabelTag>
                          </>
                        ) : (
                          <>
                            {isAdmin ? (
                              <SmallLabelTag tone="blue">admin</SmallLabelTag>
                            ) : (
                              <button
                                className="text-xs !min-h-0 !min-w-0 px-2.5 py-1 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                                onClick={() =>
                                  askConfirm({
                                    title: "Promote to admin?",
                                    message: `Make ${displayName(u)} an admin?`,
                                    tone: "blue",
                                    confirmText: "Make admin",
                                    onConfirm: () => {
                                      closeConfirm();
                                      promoteToAdmin(u.id);
                                    },
                                  })
                                }
                              >
                                Make admin
                              </button>
                            )}

                            <button
                              className="text-xs px-2.5 !min-h-0 !h-6 rounded-full bg-red-100 text-red-800 hover:bg-red-200"
                              onClick={() =>
                                askConfirm({
                                  title: "Remove member?",
                                  message: `Remove ${displayName(u)} from this circle?`,
                                  tone: "red",
                                  confirmText: "Remove",
                                  onConfirm: () => {
                                    closeConfirm();
                                    removeMember(u.id);
                                  },
                                })
                              }
                              title="Remove from circle"
                            >
                              Remove
                            </button>
                          </>
                        )}
                      </div>
                    </Row>
                  );
                })
              )}
            </div>
          </section>

          {pending !== null && (
            <section className="space-y-2">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium ml-3">
                  Pending join requests
                </h3>
                <SmallTag tone={pending.length ? "blue" : "gray"}>
                  {pending.length}
                </SmallTag>
              </div>

              <div className="rounded-2xl py-1 overflow-hidden bg-white">
                {pending.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">
                    No pending requests
                  </div>
                ) : (
                  pending.map((u) => (
                    <Row key={`pending-${u.id}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <img
                          src={u.profile_pic_url || DEFAULT_PROFILE_IMAGE}
                          alt={displayName(u)}
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div className="min-w-0">
                          <div className="text-sm font-medium truncate">
                            {displayName(u)}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          className="text-sm px-3 py-1 rounded-full bg-blue-600 text-white hover:bg-blue-700"
                          onClick={() =>
                            askConfirm({
                              title: "Approve request?",
                              message: `Approve ${displayName(u)} to join?`,
                              tone: "blue",
                              confirmText: "Approve",
                              onConfirm: () => {
                                closeConfirm();
                                approveRequest(u.id);
                              },
                            })
                          }
                        >
                          Approve
                        </button>

                        <button
                          className="text-sm px-3 py-1 rounded-full bg-red-100 text-red-800 hover:bg-red-200"
                          onClick={() =>
                            askConfirm({
                              title: "Deny request?",
                              message: `Deny ${displayName(u)}'s request?`,
                              tone: "red",
                              confirmText: "Deny",
                              onConfirm: () => {
                                closeConfirm();
                                denyRequest(u.id);
                              },
                            })
                          }
                        >
                          Deny
                        </button>
                      </div>
                    </Row>
                  ))
                )}
              </div>

              <p className="mt-1 text-xs text-gray-500 ml-3">
                Don’t see this section? Your API might not have join-request endpoints yet. It appears automatically when available.
              </p>
            </section>
          )}
        </div>
      </main>

      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        message={confirm.message}
        tone={confirm.tone}
        confirmText={confirm.confirmText}
        onCancel={closeConfirm}
        onConfirm={confirm.onConfirm || closeConfirm}
      />

      <ToastViewport toasts={toasts} remove={remove} />
    </div>
  );
}